import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GenericSvg } from "../../assets/svgs/GenericSVG";
import { CardButton } from "../../core/components/buttons/CardButton";
import { InfoText } from "../../core/components/texts/InfoText";
import { Body, H4 } from "../../core/styles/stringStyles";

import {
  getCurrentPracticeAppointmentRequestFlags,
  getCurrentPracticeId,
} from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { useHandleAppointmentBookingNavigation } from "../navigation/utils/useHandleAppointmentBookingNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import {
  getAppointmentFlowScreens,
  usePracticeSelectionSkip,
} from "./utils/appointmentFlow";

const OPENING_INFO_PRACTICE_ID = "9ae55e54-dda1-4314-b9df-d52717ed20f0";

export const AppointmentActionTypeSelectionScreen = () => {
  const { t } = useTranslation();
  const setAppointmentActionType = useMainStore(
    (state) => state.setAppointmentActionType,
  );

  const navigate = useHandleAppointmentBookingNavigation();

  const appointmentRequestFlags = useMainStore(
    getCurrentPracticeAppointmentRequestFlags,
  );
  const currentPracticeId = useMainStore(getCurrentPracticeId);

  const resetAppointmentRequestState = useMainStore(
    (state) => state.resetAppointmentState,
  );

  const { isSkipped } = usePracticeSelectionSkip();
  const flowScreens = useMainStore((state) =>
    getAppointmentFlowScreens(state, isSkipped),
  );
  const isFirstStep = flowScreens[0] === "type";

  const handleSelect = (
    actionType: "booking" | "cancellation" | "rescheduling",
  ) => {
    setAppointmentActionType(actionType);
    navigate();
  };

  useEffect(() => {
    // Reset appointment action type on mount
    setAppointmentActionType(undefined);
  }, [setAppointmentActionType]);

  useEffect(() => {
    // Reset appointment request state on mount
    resetAppointmentRequestState({ keepTherapyDepartment: true });
  }, [resetAppointmentRequestState]);

  return (
    <AppointmentScreenWrapper hideNextButton hideBackButton={isFirstStep}>
      <InfoText headline={t("APPOINTMENT_ACTION_TYPE_SCREEN_TITLE")} />
      <div>
        {/* TODO: Remove after 11.09.2026 */}
        {currentPracticeId == OPENING_INFO_PRACTICE_ID && (
          <OpeningInfoCard>
            <OpeningInfoRow>
              <OpeningInfoIcon name="Info" size={20} />
              <OpeningInfoTextContainer>
                <OpeningInfoHeadline>
                  {t("APPOINTMENT_OPENING_INFO_HEADLINE")}
                </OpeningInfoHeadline>
                <OpeningInfoText>
                  {t("APPOINTMENT_OPENING_INFO_TEXT")}
                </OpeningInfoText>
              </OpeningInfoTextContainer>
            </OpeningInfoRow>
          </OpeningInfoCard>
        )}

        {!appointmentRequestFlags?.hideBookingRequest && (
          <CardButton
            key={"booking"}
            onClick={() => handleSelect("booking")}
            title={t("APPOINTMENT_ACTION_TYPE_BOOKING_TITLE")}
          />
        )}
        {!appointmentRequestFlags?.hideCancellationRequest && (
          <CardButton
            key={"cancellation"}
            onClick={() => handleSelect("cancellation")}
            title={t("APPOINTMENT_ACTION_TYPE_CANCELLATION_TITLE")}
          />
        )}
        {!appointmentRequestFlags?.hideReschedulingRequest && (
          <CardButton
            key={"rescheduling"}
            onClick={() => handleSelect("rescheduling")}
            title={t("APPOINTMENT_ACTION_TYPE_RESCHEDULING_TITLE")}
          />
        )}
      </div>
    </AppointmentScreenWrapper>
  );
};

const OpeningInfoCard = styled.div`
  border-radius: 15px;
  background-color: #fbdb12;
  padding: 16px;
  margin-bottom: 12px;
`;

const OpeningInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const OpeningInfoIcon = styled(GenericSvg)`
  flex-shrink: 0;
  margin-top: 2px;
`;

const OpeningInfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OpeningInfoHeadline = styled(H4)`
  margin: 0 0 4px;
`;

const OpeningInfoText = styled(Body)`
  margin: 0;
  line-height: 22px;
`;
